import { TableColumn } from "react-data-table-component";
import { Fragment, useState } from "react";
import { Badges, Btn } from "../../AbstractElements";
import { RefreshCcw } from "react-feather";
import { CustomCellInterFace } from "../../Types/TablesType";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

//Modals
import UpdateDivisionModal from "../../Components/UiKits/Modal/Division/Update";

//Services
import {
  VIEW_DIVISION_QUERY_KEY,
  DIVISION_QUERY_KEY,
  divisionService,
} from "../../Services/divisionService";

//Types
import { ViewDivision } from "../../Types/CompanyProfile/Division";

const DepartmentActionCell = ({ row }: { row: ViewDivision }) => {
  const queryClient = useQueryClient();
  const [isRowLoading, setIsRowLoading] = useState<boolean>(false);
  const QueryKey = [...VIEW_DIVISION_QUERY_KEY, row.company_code];
  const handleStatusToggle = () => {
    Swal.fire({
      icon: "warning",
      html: `Are you sure you want to <b>"${
        row.status_label === "ACTIVE" ? "Deactivate " : "Activate"
      }"</b> This Division: <b>"${row.name}"</b> ?`,
      showCancelButton: true,
      cancelButtonText: "Cancel",
      cancelButtonColor: "#dc3545",
      confirmButtonText: "Yes",
      confirmButtonColor: "#0d6efd",
    }).then((result) => {
      if (result.isConfirmed) {
        setIsRowLoading(true);
        toggleMutation.mutate(row.id);
      }
    });
  };
  const toggleMutation = useMutation({
    mutationFn: divisionService.toggleStatus,
    onSuccess: async (variables: {
      id: number;
      status_label: string;
      department_name: string;
    }) => {
      queryClient.setQueryData<ViewDivision[]>(QueryKey, (oldData) =>
        oldData?.map((div) =>
          div.id === variables.id
            ? {
                ...div,
                status_label:
                  div.status_label === "ACTIVE" ? "INACTIVE" : "ACTIVE",
              }
            : div,
        ),
      );

      await queryClient.refetchQueries({
        queryKey: QueryKey,
        exact: true,
      });

      toast.success(
        `"${row.name}" status has been set to "${
          variables.status_label === "ACTIVE" ? "INACTIVE" : "ACTIVE"
        }".`,
      );
      setIsRowLoading(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to toggle status");
      setIsRowLoading(false);
    },
  });

  return (
    <div className="d-flex align-items-center gap-2">
      {/* Update Modal */}
      <UpdateDivisionModal id={row.id} />
      {/* StatusToggle Button */}
      <Btn
        size="sm"
        color={row.status_label === "ACTIVE" ? "danger" : "success"}
        className="px-2"
        onClick={handleStatusToggle}
        disabled={isRowLoading}
      >
        {isRowLoading ? (
          <>
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
            Loading...
          </>
        ) : (
          <>
            <RefreshCcw size={11} className="me-1" />
            {row.status_label === "ACTIVE" ? "Disable" : <>Enable&nbsp;</>}
          </>
        )}
      </Btn>
    </div>
  );
};

const CustomBadge: React.FC<CustomCellInterFace> = ({ label, color }) => (
  <Badges pill color={color}>
    {label}
  </Badges>
);

export const DivisionTableColumn: TableColumn<ViewDivision>[] = [
  {
    name: "DIVISION",
    selector: (row) => row.name,
    cell: (row) => (
      <div style={{ whiteSpace: "pre-line" }}>{`${row.name}`}</div>
    ),
    sortable: true,
    center: false,
  },
  {
    name: "STATUS",
    selector: (row) => row.status_label,
    cell: (row) => (
      <>
        <CustomBadge
          color={row.status_label === "ACTIVE" ? "success" : "danger"}
          label={row.status_label}
        />
      </>
    ),
    width: "100px",
    sortable: true,
    center: false,
  },
  {
    name: "DATE CREATED",
    selector: (row) => row.created_date,
    cell: (row) => (
      <div style={{ whiteSpace: "pre-line" }}>
        {row.created_by_user}
        {"\n"}
        {row.created_date}
      </div>
    ),
    width: "180px",
    sortable: true,
    center: false,
  },
  {
    name: "DATE MODIFIED",
    selector: (row) => row.updated_date,
    cell: (row) => (
      <div style={{ whiteSpace: "pre-line" }}>
        {row.updated_by_user}
        {"\n"}
        {row.updated_date}
      </div>
    ),
    width: "180px",
    sortable: true,
    center: false,
  },
  {
    name: "ACTION",
    sortable: false,
    center: false,
    cell: (row) => <DepartmentActionCell row={row} />,
    width: "200px",
  },
];
